import { Box, Button, Container, Flex, HStack, Image, Show, Text, useMediaQuery } from '@chakra-ui/react'
import {
    useGetPostInfoPreviewQuery,
    useGetPostInfoQuery,
    useGetPostSettingsQuery,
    useSetJobApplyCandidateMutation,
} from '@core/app/api/postsApi'
import { Layout } from '@core/components/Layout/Layout'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import decodeHtmlEntitiesRecursive from '@core/utils/helpers/decodeHtmlEntitiesRecursive'
import useLocalStorageStore from '@core/utils/hooks/useLocalStorageStore'
import { getLocalStorageItem } from '@core/utils/helpers/localStorageHelpers'
import { AsideInfo } from '@page/post/components/AsideInfo/AsideInfo'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { IconBeingProccesed } from '@core/assets/icons/being_proccesed'
import { IconCalendar } from '@core/assets/icons/calendar'
import { IconDeclined } from '@core/assets/icons/declined'
import { IconEmployed } from '@core/assets/icons/employed'
import { IconInpipeline } from '@core/assets/icons/in_pipeline'
import checked from '@core/assets/images/applicationExist.svg'
import noPostImage from '@core/assets/images/no-found-jp.svg'
import jobHasExpired from '@core/assets/images/job_has_expired.svg'
import { Header } from '@core/components/Header/Header'
import { useIsApplyDialog } from '@core/utils/contexts/ApplyDialogContext/ApplDialogContext'
import { ModalContext } from '@core/utils/contexts/ModalContext/ModalContext'
import { PostInfoBlock } from '@page/post/components/PostInfo/PostInfo'
import inProgressIcon from '@page/posts/assets/images/progress_loader.svg'
import { useTranslation } from 'react-i18next'

import { setResetForm } from '@core/app/slices/profile'
import { AppButton } from '@core/components/AppButton'
import { RECAPTCHA } from '@core/utils/constants/constants'
import { useAppTheme } from '@core/utils/contexts/ThemeContext/ThemeContext'
import { useAppDispatch } from '@core/utils/hooks/hook'
import { ApplyJobBlock } from '@page/post/components/ApplyJob/ApplyJob'
import ReCAPTCHA from 'react-google-recaptcha'
import useIsEmployee from '@core/utils/hooks/useIsEmployee'
import { useGetThemeQuery } from '@core/app/api/globalApi'

export const PostPage = () => {
    const isPreview = window.location.href.includes('job/preview')
    const dispatch = useAppDispatch()
    const [applyJob] = useSetJobApplyCandidateMutation()
    const [view, setView] = useState<string>('job')
    const { id } = useParams()
    const [searchParams] = useSearchParams()
    const { isEmployee } = useIsEmployee()
    const { open, collapseDialog, isApplyDialog, close, resized } = useIsApplyDialog()
    const [serviceCode] = useLocalStorageStore('service_code')
    const { onOpenApply } = useApplyV2Context()
    const applyVersion = searchParams.get('apply')
    const numberId = useMemo(() => {
        const n = Number(id)
        return isNaN(n) ? 0 : n
    }, [id])

    const customPrefix: string = document.getElementById('__custom_route_data')?.getAttribute('data-prefix') ?? ''

    const {
        data: postInfo,
        isLoading,
        isError,
    } = isPreview
        ? useGetPostInfoPreviewQuery({ getParams: window.location.search })
        : useGetPostInfoQuery({ id: numberId }, { skip: !numberId })

    const {
        data: settings,
        isSuccess,
        isLoading: isSettingsLoading,
        isError: isSettingsError,
    } = useGetPostSettingsQuery(
        // @ts-ignore
        { id, code: serviceCode },
        { skip: id === undefined }
    )
    const [isDesktop] = useMediaQuery('(min-width: 1230px)', {
        fallback: false,
    })
    const { t } = useTranslation()
    const { openModal, closeModal } = useContext(ModalContext)
    const recaptchaRef = useRef<ReCAPTCHA>(null)
    const { appTheme, updateTheme, isExpired } = useAppTheme()
    const bg = appTheme?.newSettings?.general?.Background ? `#${appTheme?.newSettings?.general?.Background}` : 'bg.3'

    const isApplyOnly = location.href.includes('apply_only')
    const currentJobId = getLocalStorageItem('apply_dialog')?.id

    const token = searchParams.get('preview_token') || undefined
    const { data: mainTheme } = useGetThemeQuery({ preview_token: token }, { skip: isExpired && !token })

    const today = new Date()
    const postDate = new Date(postInfo?.data?.ad?.Expire || '')
    const isPostEnded = postInfo?.data?.ad?.Ended || false
    const isPostExpired = postDate.getTime() < today.getTime() && postDate.getDate() !== today.getDate()
    const isPostUnavailable = isPostEnded || isPostExpired

    useEffect(() => {
        if (mainTheme) {
            updateTheme(mainTheme)
        }
    }, [mainTheme])

    const isApplyShowed =
        isSuccess &&
        settings.application &&
        Object.keys(settings?.application || {}).length === 0 &&
        (!resized || !isApplyDialog)

    const getDifference = (expireDate: string) => {
        const date1: Date = new Date(expireDate)
        const date2: Date = new Date()

        // @ts-ignore
        const diffInMs = Math.abs(date2 - date1)

        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

        if (diffInDays === 1) {
            return `(${t('2839')})`
        } else {
            return ''
        }
    }

    const months = [
        `${t('489')}`,
        `${t('490')}`,
        `${t('491')}`,
        `${t('492')}`,
        `${t('493')}`,
        `${t('494')}`,
        `${t('495')}`,
        `${t('496')}`,
        `${t('497')}`,
        `${t('498')}`,
        `${t('499')}`,
        `${t('500')}`,
    ]

    const isOS = () => {
        const userAgent = navigator.userAgent
        if (/iPad|iPhone|iPod/.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
            return 'ios'
        }
        return 'unknown'
    }

    const submitHandler = async () => {
        recaptchaRef.current?.reset()
        let token = await recaptchaRef.current?.executeAsync()
        if (id && token) {
            try {
                await applyJob({
                    id,
                    data: {
                        'g-recaptcha-response': token,
                    },
                }).unwrap()

                close()
                localStorage.setItem('after_apply', '1')
                localStorage.setItem('after_apply_id', id)
                localStorage.setItem('after_apply_title', decodeHtmlEntitiesRecursive(postInfo?.data?.ad?.JobTitle))
                location.href = '/profile'
            } catch (error: any) {}
        }
    }

    const openApplyWindowHandler = () => {
        if (settings?.externalLink && settings?.externalLink?.length > 0) {
            const externalLink = settings.externalLink
            if (externalLink.startsWith('mailto:')) window.open(settings?.externalLink, '_blank')
            else {
                const link = settings?.externalLink.includes('http')
                    ? settings?.externalLink
                    : `https://${settings?.externalLink}`
                window.open(link, '_blank')?.focus()
            }
            return
        }

        const isNewApplyEnabled =
            postInfo?.data?.isNewApplyEnabled ??
            (document?.querySelector('input[name="__new_apply_form_enabled"]') as HTMLInputElement)?.value === '1'

        // TODO -start- Apply v2
        if (applyVersion === 'v2' || isNewApplyEnabled) {
            onOpenApply()
            return
        }
        // TODO -end- Apply v2

        if (
            !settings?.externalAuth &&
            !settings?.askAttributes &&
            settings?.screeningQuestions?.length === 0 &&
            !settings.settings?.anonymousApplication &&
            // !settings.requiredFields?.applicationText &&
            !settings.fields?.applicationText.visible &&
            settings.isUser
        ) {
            submitHandler()
            return
        }

        if (isApplyDialog) {
            if (id !== currentJobId) {
                openModal({
                    size: 'md',
                    isHeaderVisible: false,
                    isFooterVisible: false,
                    body: (
                        <Box p={6}>
                            <Text fontSize={'2xl'} fontWeight={'semibold'} color={'text.main'}>
                                {t('7399')}
                            </Text>
                            <Text color={'text.secondary'} mt={4}>
                                {t('7400')} {t('7401')},{' '}
                                <Text display={'inline'} fontWeight={'semibold'}>
                                    {t('7402')}
                                </Text>
                            </Text>
                            <Text color={'text.secondary'} mt={4}>
                                {t('7403')}
                            </Text>
                            <Flex
                                w={'full'}
                                flexDirection={'column'}
                                gap={3}
                                justifyContent={'space-between'}
                                alignItems={'center'}
                                mt={'40px'}
                            >
                                <Button
                                    variant={'error'}
                                    fontSize={'sm'}
                                    w={'full'}
                                    ra-data-job-post-id={id}
                                    id={'ra-button-job-post-action-application-form-close-ok'}
                                    onClick={() => {
                                        dispatch(setResetForm(true))

                                        setTimeout(() => {
                                            closeModal()
                                            localStorage.removeItem('mobile_verification')
                                        }, 0)

                                        open(id ? id : '', {
                                            title: decodeHtmlEntitiesRecursive(postInfo?.data?.ad?.JobTitle),
                                            link: `/job/${id}`,
                                            expiredDate: postInfo?.data?.ad?.Expire || '',
                                        })
                                    }}
                                >
                                    {t('7404')}
                                </Button>
                                <Button w={'full'} variant={'secondary'} onClick={closeModal}>
                                    {t('7405')}
                                </Button>
                            </Flex>
                        </Box>
                    ),
                })
            } else {
                collapseDialog()
            }
        } else {
            open(id ? id : '', {
                title: decodeHtmlEntitiesRecursive(postInfo?.data?.ad?.JobTitle),
                link: `/job/${id}`,
                expiredDate: postInfo?.data?.ad.Expire || '',
            })
        }
    }

    useEffect(() => {
        const path = searchParams.get('path')
        const service_code = searchParams.get('code')

        if (path) {
            localStorage.setItem('apply_path', path)
        }

        if (service_code && id) {
            open(id, {
                title: decodeHtmlEntitiesRecursive(postInfo?.data?.ad?.JobTitle),
                link: `/job/${id}`,
                expiredDate: postInfo?.data?.ad?.Expire || '',
            })
        }
    }, [searchParams])

    if (isApplyOnly && !settings?.isUser) {
        return (
            <>
                {!isError && !isSettingsError ? (
                    <Layout
                        showFooter={false}
                        showHeader={false}
                        useContainer={isDesktop ? true : false}
                        useMobileHeader={false}
                    >
                        <Flex minH={'100vh'} flexDirection={'column'} bg={bg}>
                            <Flex
                                as={'main'}
                                flexDirection={'column'}
                                w={'full'}
                                alignItems={'center'}
                                justifyContent={'center'}
                                h={'full'}
                                flex={1}
                                py={'80px'}
                            >
                                <Container maxW={{ base: 'container.sm', xl: 'container.xl' }}>
                                    <Flex flexDirection={'column'} gap={'64px'} w={'full'} alignItems={'center'}>
                                        {appTheme?.headerLogo && (
                                            <Image
                                                maxW={{ base: '160px', xl: '200px' }}
                                                maxH={'80px'}
                                                src={appTheme?.headerLogo}
                                                alt={`${t('7840')}`}
                                            />
                                        )}
                                        <Box
                                            boxShadow={'0px 1px 6px 0px rgba(0,0,0,0.15)'}
                                            w={{ base: 'auto', xl: '800px' }}
                                            p={4}
                                            borderRadius={'8px'}
                                            bg={'bg.1'}
                                        >
                                            <ApplyJobBlock />
                                        </Box>
                                    </Flex>
                                </Container>
                            </Flex>
                        </Flex>
                    </Layout>
                ) : (
                    <Flex minH={'100vh'} flexDirection={'column'} bg={bg}>
                        <Header />
                        <Flex
                            as={'main'}
                            flexDirection={'column'}
                            w={'full'}
                            alignItems={'center'}
                            justifyContent={'center'}
                            h={'full'}
                            flex={1}
                        >
                            <Flex w={'336px'} flexDirection={'column'} alignItems={'center'} h={'full'}>
                                <Image src={noPostImage} alt={`${t('7841')}`} />
                                <Text
                                    fontSize={'24px'}
                                    fontWeight={'semibold'}
                                    lineHeight={'32px'}
                                    textAlign={'center'}
                                    mt={6}
                                >
                                    {t('6924')}
                                </Text>
                                <Text fontSize={'sm'} fontWeight={'medium'} color={'text.thirdly'} mt={2}>
                                    {t('6925')}
                                </Text>
                                <AppButton
                                    translate="no"
                                    title={t('6926')}
                                    variant={'primary'}
                                    w={'184px'}
                                    mt={6}
                                    onClick={() => (location.href = '/jobs')}
                                />
                            </Flex>
                        </Flex>
                    </Flex>
                )}
            </>
        )
    } else {
        return (
            <>
                {!isError ? (
                    <>
                        <Layout useContainer={isDesktop ? true : false} useMobileHeader={false}>
                            <ReCAPTCHA
                                sitekey={RECAPTCHA}
                                ref={recaptchaRef}
                                size={'invisible'}
                                onExpired={() => {
                                    recaptchaRef.current?.reset()
                                }}
                                onError={console.log}
                            />
                            <Box as={'section'} pt={{ base: 2, xl: 10 }} pb={{ base: '80px', xl: 10 }}>
                                <Flex justifyContent={'space-between'}>
                                    <Box borderRadius={'8px'} w={{ base: '100%', xl: '800px' }}>
                                        {isPostUnavailable && (
                                            <Box
                                                bg={'bg.1'}
                                                borderRadius={{
                                                    base: '0px',
                                                    xl: '8px',
                                                }}
                                                py={{ base: '18px', xl: '22px' }}
                                                px={{ base: 4, xl: 6 }}
                                                mb={{ base: 2, xl: 4 }}
                                                boxShadow={'0px 1px 6px 0px rgba(0,0,0,0.15)'}
                                                display={'flex'}
                                                alignItems={'center'}
                                                flexDirection={{ base: 'column', md: 'row' }}
                                                gap={'20px'}
                                            >
                                                <Image src={jobHasExpired} width={'72px'} alt={`${t('4720')}`} />
                                                <Flex flexDirection={'column'} flexGrow={1}>
                                                    <Text
                                                        color={'#ba5b1c'}
                                                        display={'inline-flex'}
                                                        gap={'8px'}
                                                        alignItems={'center'}
                                                        fontWeight={'600'}
                                                    >
                                                        <i className="fa-solid fa-triangle-exclamation"></i>
                                                        {isPostEnded
                                                            ? t('9973', 'Job has been archived')
                                                            : t('8727', 'Job has expired')}
                                                    </Text>
                                                    <Text fontSize={'14px'} fontWeight={'500'}>
                                                        {isPostEnded
                                                            ? `${t(
                                                                  '9974',
                                                                  'This job posting has been archived and you can no longer apply'
                                                              )}.`
                                                            : t(
                                                                  '8728',
                                                                  'This job posting has expired and you can no longer apply.'
                                                              )}
                                                    </Text>
                                                    <Text fontSize={'14px'} fontWeight={'500'}>
                                                        {t('8729', 'Click the button to view all available jobs.')}
                                                    </Text>
                                                </Flex>
                                                <AppButton
                                                    translate="no"
                                                    variant={'primary'}
                                                    minW={'132px'}
                                                    mt={6}
                                                    onClick={() => (location.href = '/jobs')}
                                                >
                                                    {t('8730', 'View all jobs')}
                                                </AppButton>
                                            </Box>
                                        )}
                                        <Box>
                                            {isSuccess &&
                                                settings.application &&
                                                Object.keys(settings.application || {}).length > 0 && (
                                                    <Box
                                                        key={settings.application?.AdID}
                                                        bg={'bg.1'}
                                                        borderRadius={{
                                                            base: '0px',
                                                            xl: '8px',
                                                        }}
                                                        py={{ base: '18px', xl: '22px' }}
                                                        px={{ base: 4, xl: 6 }}
                                                        mb={{ base: 2, xl: 4 }}
                                                        boxShadow={'0px 1px 6px 0px rgba(0,0,0,0.15)'}
                                                    >
                                                        {settings.application?.Active === 1 ? (
                                                            <Flex gap={'2'}>
                                                                <Image src={checked} maxW={4} alt={`${t('2450')}`} />
                                                                <Text fontWeight={'semibold'} color={'text.success'}>
                                                                    {t('2450')}
                                                                </Text>
                                                            </Flex>
                                                        ) : (
                                                            <Flex gap={'2'}>
                                                                <Image
                                                                    src={inProgressIcon}
                                                                    maxW={4}
                                                                    alt={`${t('4720')}`}
                                                                />
                                                                <Text fontWeight={'semibold'} color={'text.warning'}>
                                                                    {t('4720')}
                                                                </Text>
                                                            </Flex>
                                                        )}

                                                        {settings.application?.ApplicationText && (
                                                            <Box mt={5}>
                                                                <Text fontSize={'sm'} color={'text.main'}>
                                                                    {t('1221')}
                                                                </Text>
                                                                <Text fontSize={'sm'} color={'text.secondary'} mt={2}>
                                                                    {settings.application?.ApplicationText}
                                                                </Text>
                                                            </Box>
                                                        )}
                                                        <Flex
                                                            justifyContent={'space-between'}
                                                            alignItems={'center'}
                                                            mt={5}
                                                        >
                                                            {settings.application.Applied !== '0000-00-00 00:00:00' && (
                                                                <HStack>
                                                                    <IconCalendar />
                                                                    <Text fontSize={'sm'} color={'text.thirdly'}>
                                                                        {t('1215')}:
                                                                    </Text>
                                                                    <Text fontSize={'sm'} color={'text.thirdly'}>
                                                                        {`${
                                                                            months[
                                                                                +settings.application?.Applied?.split(
                                                                                    ' '
                                                                                )[0].split('-')[1] - 1
                                                                            ]
                                                                        } ${+settings.application?.Applied?.split(
                                                                            ' '
                                                                        )[0].split('-')[2]} ${getDifference(
                                                                            settings.application?.Applied?.split(' ')[0]
                                                                        )}`}
                                                                    </Text>
                                                                </HStack>
                                                            )}

                                                            <Box>
                                                                {settings.application?.ApplicationStatusID === 0 && (
                                                                    <HStack alignItems={'center'} ml={'auto'}>
                                                                        <IconDeclined />
                                                                        <Text
                                                                            fontSize={'sm'}
                                                                            fontWeight={'medium'}
                                                                            color={'text.thirdly'}
                                                                        >
                                                                            {t('1579')}
                                                                        </Text>
                                                                    </HStack>
                                                                )}
                                                                {settings.application?.ApplicationStatusID === 1 && (
                                                                    <HStack alignItems={'center'} ml={'auto'}>
                                                                        <IconBeingProccesed />
                                                                        <Text
                                                                            fontSize={'sm'}
                                                                            fontWeight={'medium'}
                                                                            color={'text.thirdly'}
                                                                        >
                                                                            {t('6826')}
                                                                        </Text>
                                                                    </HStack>
                                                                )}
                                                                {settings.application?.ApplicationStatusID === 2 && (
                                                                    <HStack alignItems={'center'} ml={'auto'}>
                                                                        <IconInpipeline />
                                                                        <Text
                                                                            fontSize={'sm'}
                                                                            fontWeight={'medium'}
                                                                            color={'text.thirdly'}
                                                                        >
                                                                            {t('99')}
                                                                        </Text>
                                                                    </HStack>
                                                                )}
                                                                {settings.application?.ApplicationStatusID === 3 && (
                                                                    <HStack alignItems={'center'} ml={'auto'}>
                                                                        <IconEmployed />
                                                                        <Text
                                                                            fontSize={'sm'}
                                                                            fontWeight={'medium'}
                                                                            color={'text.thirdly'}
                                                                        >
                                                                            {t('43')}
                                                                        </Text>
                                                                    </HStack>
                                                                )}
                                                            </Box>
                                                        </Flex>
                                                    </Box>
                                                )}
                                            <Show breakpoint={'(max-width: 1229px)'}>
                                                <Flex w={'full'}>
                                                    <Box
                                                        bg={'bg.1'}
                                                        p={3}
                                                        flex={1}
                                                        borderBottom={view === 'job' ? '2px solid' : '1px solid'}
                                                        borderColor={view === 'job' ? 'state.primary' : 'border.4'}
                                                        onClick={() => {
                                                            setView('job')
                                                        }}
                                                        cursor={'pointer'}
                                                    >
                                                        <Text fontWeight={'semibold'} textAlign={'center'}>
                                                            {isApplyDialog ? `${t('1589')}` : `${t('730')}`}
                                                        </Text>
                                                    </Box>
                                                    <Box
                                                        bg={'bg.1'}
                                                        p={3}
                                                        flex={1}
                                                        borderBottom={view === 'about' ? '2px solid' : '1px solid'}
                                                        borderColor={view === 'about' ? 'state.primary' : 'border.4'}
                                                        onClick={() => setView('about')}
                                                        cursor={'pointer'}
                                                    >
                                                        <Text fontWeight={'semibold'} textAlign={'center'}>
                                                            {t('2492')}
                                                        </Text>
                                                    </Box>
                                                </Flex>
                                            </Show>
                                            <Show below={'xl'}>
                                                {view === 'job' ? (
                                                    <PostInfoBlock
                                                        data={postInfo?.data}
                                                        loaded={!isLoading}
                                                        isPostExpired={isPostUnavailable}
                                                        isPostArchived={isPostEnded}
                                                    />
                                                ) : (
                                                    <AsideInfo
                                                        data={postInfo?.data}
                                                        loaded={!isLoading}
                                                        isPostExpired={isPostUnavailable}
                                                        changeView={() => {
                                                            id &&
                                                                open(id, {
                                                                    title: decodeHtmlEntitiesRecursive(
                                                                        postInfo?.data?.ad?.JobTitle
                                                                    ),
                                                                    link: `/job/${id}`,
                                                                    expiredDate: postInfo?.data?.ad?.Expire || '',
                                                                })
                                                        }}
                                                    />
                                                )}
                                            </Show>
                                            <Show above={'xl'}>
                                                <PostInfoBlock
                                                    data={postInfo?.data}
                                                    loaded={!isLoading}
                                                    isPostExpired={isPostUnavailable}
                                                    isPostArchived={isPostEnded}
                                                />
                                            </Show>
                                        </Box>
                                    </Box>
                                    <Show above={'xl'}>
                                        <Box borderRadius={'8px'} w={'384px'}>
                                            <AsideInfo
                                                data={postInfo?.data}
                                                loaded={!isLoading}
                                                isPostExpired={isPostUnavailable}
                                                changeView={() =>
                                                    id &&
                                                    open(id, {
                                                        title: decodeHtmlEntitiesRecursive(
                                                            postInfo?.data?.ad?.JobTitle
                                                        ),
                                                        link: `/job/${id}`,
                                                        expiredDate: postInfo?.data?.ad?.Expire || '',
                                                    })
                                                }
                                            />
                                        </Box>
                                    </Show>
                                </Flex>
                            </Box>
                        </Layout>
                        <Show breakpoint="(max-width: 1229px)">
                            {isApplyShowed && (
                                <Box
                                    position={'fixed'}
                                    w={'full'}
                                    bottom={0}
                                    left={0}
                                    right={0}
                                    py={2}
                                    px={4}
                                    pt={4}
                                    pb={isOS() === 'ios' ? '32px' : 4}
                                    bg={'bg.1'}
                                    transform={'translate3d(0,0,0)'}
                                    zIndex={'sticky'}
                                    borderTop={'1px solid'}
                                    borderColor={'border.1'}
                                >
                                    <AppButton
                                        translate="no"
                                        variant={'primary'}
                                        title={t('6634', 'Apply now')
                                            .toLowerCase()
                                            .replace(/^\w/, (c) => c.toUpperCase())}
                                        ra-data-job-post-id={id}
                                        id={'ra-button-job-post-action-apply-now'}
                                        w={'full'}
                                        onClick={() => id && openApplyWindowHandler()}
                                    />
                                </Box>
                            )}
                        </Show>
                    </>
                ) : (
                    <Flex minH={'100vh'} flexDirection={'column'} bg={bg}>
                        <Header />
                        <Flex
                            as={'main'}
                            flexDirection={'column'}
                            w={'full'}
                            alignItems={'center'}
                            justifyContent={'center'}
                            h={'full'}
                            flex={1}
                        >
                            <Flex w={'336px'} flexDirection={'column'} alignItems={'center'} h={'full'}>
                                <Image src={noPostImage} alt={`${t('7841')}`} />
                                <Text
                                    fontSize={'24px'}
                                    fontWeight={'semibold'}
                                    lineHeight={'32px'}
                                    textAlign={'center'}
                                    mt={6}
                                >
                                    {t('6924')}
                                </Text>
                                <Text fontSize={'sm'} fontWeight={'medium'} color={'text.thirdly'} mt={2}>
                                    {t('6925')}
                                </Text>
                                <AppButton
                                    translate="no"
                                    title={t('6926')}
                                    variant={'primary'}
                                    w={'184px'}
                                    mt={6}
                                    onClick={() => (location.href = '/jobs')}
                                />
                            </Flex>
                        </Flex>
                    </Flex>
                )}
            </>
        )
    }
}
